import { Box, Button, Theme, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useFormContext } from "react-hook-form";
import { FieldProps } from "@/core/components/Field";
import Image from "next/image";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

interface DefaultValue {
  numberOfPassengers?: string;
  variableName?: string;
}

const styles = (theme: Theme) => ({
  passengersWrapper: {
    width: "80px",
    height: "48px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey.A400}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.background.default,
  },
  buttons: {
    minWidth: "48px",
    height: "48px",
    borderColor: theme.palette.grey[800],
    backgroundColor: theme.palette.background.default,
  },
  iconsWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: "290px",
    margin: "0 auto",
    marginTop: theme.spacing(3),
  },
});

const MAX_PASSENGERS = 20;
const MIN_PASSENGERS = 1;
const DEFAULT_PASSENGERS = 2;

const RenderPassengerIcons = ({
  passangerAmount,
}: {
  passangerAmount: number;
}) => {
  const icons: null[] = new Array(passangerAmount).fill(null);
  const uniqueKeyId: number = Date.now();
  return (
    <>
      {icons.map((_, i) => (
        <Image
          key={`${uniqueKeyId + i}`}
          src={`images/icons/cash.svg`}
          alt="passenger-with-cash"
          width="60"
          height="60"
        />
      ))}
    </>
  );
};

const parseDefaultValue = (fieldDefaultValue?: string): DefaultValue | null => {
  if (!fieldDefaultValue) return null;

  const isNumber = !isNaN(+fieldDefaultValue);
  if (isNumber)
    return { numberOfPassengers: fieldDefaultValue, variableName: undefined };

  const isSingleVariableName = !fieldDefaultValue.includes("||");
  if (isSingleVariableName)
    return { numberOfPassengers: undefined, variableName: fieldDefaultValue };

  const [variableName, numberOfPassengers] = fieldDefaultValue.split("||");
  if (!variableName || !numberOfPassengers) return null;

  return {
    variableName: variableName.trim(),
    numberOfPassengers: numberOfPassengers.trim(),
  };
};

export const PassengersNumber = ({ field }: FieldProps) => {
  const [passengersNumber, setPassengersNumber] = useState(DEFAULT_PASSENGERS);
  const classes: any = useCustomStyles(styles);
  const { setValue } = useFormContext();

  const defaultValue = parseDefaultValue(field.defaultValue);
  const defaultNumberOfPassengers =
    useDefaultValue(defaultValue?.variableName) ??
    defaultValue?.numberOfPassengers;

  useEffect(() => {
    if (defaultNumberOfPassengers) {
      setPassengersNumber(+defaultNumberOfPassengers);
    }
  }, [defaultNumberOfPassengers]);

  useEffect(() => {
    setValue(field.key, passengersNumber);
  }, [passengersNumber]);

  const incrementPassengers = () => {
    const maxPassengersNumber = field.validate?.max || MAX_PASSENGERS;
    if (passengersNumber < maxPassengersNumber) {
      setPassengersNumber(passengersNumber + 1);
    }
  };

  const decrementPassengers = () => {
    const minPassengersNumber = field.validate?.min || MIN_PASSENGERS;
    if (passengersNumber > minPassengersNumber) {
      setPassengersNumber(passengersNumber - 1);
    }
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        p={0}
      >
        <Button
          data-cy="decrement"
          sx={classes?.buttons}
          variant="outlined"
          size="small"
          onClick={decrementPassengers}
        >
          <RemoveIcon color="secondary" />
        </Button>
        <Box sx={classes?.passengersWrapper}>
          <Typography variant="h2">{passengersNumber}</Typography>
        </Box>
        <Button
          data-cy="increment"
          sx={classes?.buttons}
          variant="outlined"
          size="small"
          onClick={incrementPassengers}
        >
          <AddIcon color="secondary" />
        </Button>
      </Box>
      <Box sx={classes?.iconsWrapper}>
        <RenderPassengerIcons passangerAmount={passengersNumber} />
      </Box>
      <Typography variant="h3" align="center" mt={2}>
        {t("numberOfPassengers.label")}
      </Typography>
    </>
  );
};
